// URL tampilan media produk — helper MURNI (temuan 5 uji restore 2026-08).
//
// LATAR: `product_media.media_url` berisi URL ABSOLUT yang mengunci ref project
// Supabase saat file di-upload (https://<ref-lama>.supabase.co/storage/...).
// Restore/migrasi ke project lain membuat seluruh gambar menunjuk project lama.
// Migrasi 2300 mem-backfill `storage_path` (path relatif bucket product-media,
// konvensi sama dengan `productMediaStoragePath` di duplicateMedia.ts); helper
// ini menyusun URL tampilan dari project AKTIF (NEXT_PUBLIC_SUPABASE_URL) +
// storage_path, dan FALLBACK ke `media_url` apa adanya bila storage_path
// kosong (baris belum ter-backfill / URL non-storage hasil import web lama) —
// perilaku identik dengan sebelum helper ada.

import { PRODUCT_MEDIA_BUCKET } from "./duplicateMedia";

export type ProductMediaUrlRow = {
  media_url?: string | null;
  storage_path?: string | null;
};

// Normalkan encoding satu segmen path: decode dulu (bila sudah ter-encode)
// lalu encode ulang, supaya "a b.jpg" maupun "a%20b.jpg" menghasilkan URL yang
// sama & valid tanpa dobel-encode (%2520). Encoding rusak (mis. '%' nyasar,
// decode melempar) → segmen dipakai apa adanya lalu di-encode.
function encodePathSegment(segment: string): string {
  let decoded = segment;
  try {
    decoded = decodeURIComponent(segment);
  } catch {
    // biarkan apa adanya
  }
  return encodeURIComponent(decoded);
}

/**
 * URL objek publik Supabase Storage dari base project + path dalam bucket.
 * null bila base atau path kosong — pemanggil fallback ke media_url.
 */
export function buildPublicStorageUrl(
  baseUrl: string | null | undefined,
  storagePath: string | null | undefined,
  bucket: string = PRODUCT_MEDIA_BUCKET
): string | null {
  const base = (baseUrl ?? "").trim().replace(/\/+$/, "");
  const path = (storagePath ?? "").trim().replace(/^\/+/, "");
  if (!base || !path) return null;
  const encoded = path.split("/").map(encodePathSegment).join("/");
  return `${base}/storage/v1/object/public/${bucket}/${encoded}`;
}

/**
 * URL tampilan satu baris `product_media`: dibangun dari project AKTIF +
 * `storage_path` bila keduanya ada; selain itu `media_url` apa adanya
 * (perilaku lama — baris belum ter-backfill tampil persis seperti dulu).
 *
 * `baseUrl` opsional untuk pengujian/override; tanpa argumen memakai
 * NEXT_PUBLIC_SUPABASE_URL (dibaca lazy agar bisa di-stub di unit test;
 * ekspresi literal `process.env.NEXT_PUBLIC_*` tetap di-inline Next di bundle
 * klien). Meneruskan null/"" secara eksplisit = tanpa base → selalu fallback.
 */
export function resolveProductMediaUrl(
  row: ProductMediaUrlRow | null | undefined,
  baseUrl?: string | null
): string | null {
  if (!row) return null;
  const base =
    baseUrl !== undefined ? baseUrl : process.env.NEXT_PUBLIC_SUPABASE_URL;
  const built = buildPublicStorageUrl(base, row.storage_path);
  if (built) return built;
  return typeof row.media_url === "string" ? row.media_url : null;
}

/**
 * Foto COVER sebuah produk dari baris-baris `product_media`-nya, sudah jadi URL
 * siap tampil.
 *
 * Aturannya (urutan ini SUDAH dipakai di dua tempat sebelum diekstrak ke sini —
 * `mapBoardOrderRow` & `mapCustomerCartRow`; salinan ketiga pasti menyimpang):
 * gambar ber-`is_cover` menang → kalau tak ada, gambar PERTAMA → kalau tak ada
 * gambar sama sekali, null. Baris non-`image` (video) tak pernah dipilih.
 *
 * `null` = produk memang tak punya foto — pemanggil WAJIB membedakannya dari
 * string kosong dan tak merender bingkai gambar yang isinya tak pernah datang.
 */
export function pickProductCoverUrl(
  rows: ReadonlyArray<ProductMediaUrlRow & { media_type?: string | null; is_cover?: boolean | null }> | null | undefined,
  baseUrl?: string | null
): string | null {
  const list = rows ?? [];
  const cover =
    list.find((m) => m?.media_type === "image" && m?.is_cover) ??
    list.find((m) => m?.media_type === "image") ??
    null;
  if (!cover) return null;
  return resolveProductMediaUrl(cover, baseUrl);
}
